import {
  Badge,
  Button,
  Empty,
  Flex,
  Input,
  Popconfirm,
  Skeleton,
  Tooltip,
  Typography
} from 'antd'
import dayjs from 'dayjs'
import { Check, MessageSquare, Pencil, Search, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { hasPendingResolution, useStyles } from 'src/components/chat'
import type { Conversation } from 'src/components/chat'
import {
  useIntersectionObserver,
  useOverlayActions,
  useScrollFade
} from 'src/hooks'
import { formatAuto } from 'src/utils/dateTime'

const { Text } = Typography

const HISTORY_LOADING_ROWS = 2
const RELATIVE_LABEL_REFRESH_MS = 10_000
const LOAD_MORE_ROOT_MARGIN = '96px'
const TITLE_MAX_LENGTH = 200

type Props = {
  conversations: Conversation[]
  activeId: string | null
  onSelect: (id: string) => void
  onDelete: (id: string) => void
  onRename: (id: string, title: string) => void
  search?: string
  onSearchChange?: (value: string) => void
  loading?: boolean
  loadingMore?: boolean
  hasMore?: boolean
  onLoadMore?: () => void
  stacked?: boolean
}

type HistoryRowProps = {
  conversation: Conversation
  active: boolean
  stacked: boolean
  updatedAtLabel: string
  deleteLabel: string
  deleteConfirmLabel: string
  cancelLabel: string
  editLabel: string
  processingLabel: string
  awaitingLabel: string
  onSelect: (id: string) => void
  onDelete: (id: string) => void
  onRename: (id: string, title: string) => void
}

const ChatHistoryRow = ({
  conversation,
  active,
  stacked,
  updatedAtLabel,
  deleteLabel,
  deleteConfirmLabel,
  cancelLabel,
  editLabel,
  processingLabel,
  awaitingLabel,
  onSelect,
  onDelete,
  onRename
}: HistoryRowProps) => {
  const { styles, cx } = useStyles()
  const { getRootProps, getActionProps } = useOverlayActions()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(conversation.title)
  const cancelledRef = useRef(false)

  const streaming =
    conversation.status === 'running' ||
    conversation.status === 'pending' ||
    conversation.messages.some((m) => m.live)
  const awaitingInput =
    conversation.status === 'paused' || hasPendingResolution(conversation)

  const startEdit = () => {
    cancelledRef.current = false
    setDraft(conversation.title)
    setEditing(true)
  }

  const commitEdit = () => {
    if (cancelledRef.current) {
      cancelledRef.current = false

      return
    }
    const next = draft.trim()
    if (next && next !== conversation.title) onRename(conversation.id, next)
    setEditing(false)
  }

  const cancelEdit = () => {
    cancelledRef.current = true
    setEditing(false)
  }

  if (editing) {
    return (
      <Flex align="center" className={styles.historyRow}>
        <Input
          size="small"
          autoFocus
          allowClear
          maxLength={TITLE_MAX_LENGTH}
          value={draft}
          aria-label={editLabel}
          className={styles.historyRowInput}
          onChange={(event) => setDraft(event.target.value)}
          onPressEnter={commitEdit}
          onBlur={commitEdit}
          onKeyDown={(event) => {
            if (event.key === 'Escape') cancelEdit()
          }}
        />
      </Flex>
    )
  }

  return (
    <Flex
      align="center"
      gap={8}
      className={styles.historyRow}
      data-active={active || undefined}
      {...getRootProps()}
      data-actions-open={confirmOpen || undefined}
    >
      <button
        type="button"
        className={styles.historyRowMain}
        onClick={() => onSelect(conversation.id)}
      >
        {streaming ? (
          <Tooltip title={processingLabel}>
            <span className={styles.historyRowStatus}>
              <Badge status="processing" />
            </span>
          </Tooltip>
        ) : awaitingInput ? (
          <Tooltip title={awaitingLabel}>
            <span className={styles.historyRowStatus}>
              <Badge status="warning" />
            </span>
          </Tooltip>
        ) : (
          <MessageSquare size={13} className={styles.historyRowIcon} />
        )}
        {stacked ? (
          <span className={styles.historyRowText}>
            <Text
              className={styles.historyRowTitle}
              ellipsis={{ tooltip: conversation.title }}
            >
              {conversation.title}
            </Text>
            <Text type="secondary" className={styles.historyRowTime}>
              {updatedAtLabel}
            </Text>
          </span>
        ) : (
          <>
            <Text
              className={styles.historyRowTitle}
              ellipsis={{ tooltip: conversation.title }}
            >
              {conversation.title}
            </Text>
            <span className={cx(styles.historyRowMeta, 'skx-history-meta')}>
              {active ? (
                <Check size={13} className={styles.historyRowCheck} />
              ) : null}
              <Text type="secondary" className={styles.historyRowTime}>
                {updatedAtLabel}
              </Text>
            </span>
          </>
        )}
      </button>
      <Flex
        align="center"
        className={cx(
          styles.historyRowActions,
          'skx-history-actions',
          stacked && styles.historyRowActionsStacked
        )}
      >
        <Button
          type="text"
          size="small"
          icon={<Pencil size={14} />}
          aria-label={editLabel}
          className={styles.historyRowActionButton}
          {...getActionProps<HTMLElement>()}
          onClick={(event) => {
            event.stopPropagation()
            startEdit()
          }}
        />
        <Popconfirm
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title={deleteConfirmLabel}
          okText={deleteLabel}
          cancelText={cancelLabel}
          okButtonProps={{ danger: true }}
          onConfirm={() => onDelete(conversation.id)}
        >
          <Button
            danger
            type="text"
            size="small"
            icon={<Trash2 size={14} />}
            aria-label={deleteLabel}
            {...getActionProps<HTMLElement>()}
          />
        </Popconfirm>
      </Flex>
    </Flex>
  )
}

const ChatHistorySkeletonRows = () => {
  const { styles } = useStyles()

  return (
    <Flex vertical gap={2} className={styles.historySkeletonList}>
      {Array.from({ length: HISTORY_LOADING_ROWS }).map((_, index) => (
        <Flex
          key={index}
          align="center"
          gap={8}
          className={styles.historySkeletonRow}
        >
          <Skeleton.Avatar
            active
            size={13}
            shape="square"
            className={styles.historySkeletonIcon}
          />
          <Skeleton.Input
            active
            block
            size="small"
            className={styles.historySkeletonTitle}
          />
        </Flex>
      ))}
    </Flex>
  )
}

const groupByCalendarDay = (items: Conversation[]) => {
  const startOfToday = dayjs().startOf('day')
  const startOfYesterday = startOfToday.subtract(1, 'day')
  const today: Conversation[] = []
  const yesterday: Conversation[] = []
  const older: Conversation[] = []

  for (const c of items) {
    const day = dayjs(c.updatedAt)
    if (!day.isBefore(startOfToday)) today.push(c)
    else if (!day.isBefore(startOfYesterday)) yesterday.push(c)
    else older.push(c)
  }

  return { today, yesterday, older }
}

export const ChatHistoryPanel = ({
  conversations,
  activeId,
  onSelect,
  onDelete,
  onRename,
  search,
  onSearchChange,
  loading = false,
  loadingMore = false,
  hasMore = false,
  onLoadMore,
  stacked = false
}: Props) => {
  const { styles, cx } = useStyles()
  const { t } = useTranslation()
  const searchable = Boolean(onSearchChange)
  const searching = Boolean(search)

  const groups = useMemo(
    () => groupByCalendarDay(conversations),
    [conversations]
  )

  const [, setRelativeLabelTick] = useState(0)
  useEffect(() => {
    const id = window.setInterval(
      () => setRelativeLabelTick((n) => n + 1),
      RELATIVE_LABEL_REFRESH_MS
    )

    return () => window.clearInterval(id)
  }, [])

  const { ref: fadeRef, className: fadeClassName } = useScrollFade()
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const setScrollRef = useCallback(
    (node: HTMLDivElement | null) => {
      scrollRef.current = node
      fadeRef(node)
    },
    [fadeRef]
  )

  const { observerElement } = useIntersectionObserver({
    rootRef: scrollRef,
    onIntersect: () => onLoadMore?.(),
    enabled: Boolean(hasMore && !loading && !loadingMore),
    rootMargin: LOAD_MORE_ROOT_MARGIN
  })

  const renderGroup = (label: string, items: Conversation[]) => {
    if (items.length === 0) return null

    return (
      <Flex vertical gap={2} key={label}>
        <Text className={styles.historyGroupLabel}>{label}</Text>
        {items.map((conversation) => (
          <ChatHistoryRow
            key={conversation.id}
            conversation={conversation}
            active={conversation.id === activeId}
            stacked={stacked}
            updatedAtLabel={formatAuto(conversation.updatedAt)}
            deleteLabel={t('common.delete')}
            deleteConfirmLabel={t('workspaceHome.history.deleteConfirm')}
            cancelLabel={t('common.cancel')}
            editLabel={t('common.edit')}
            processingLabel={t('workspaceHome.history.processing')}
            awaitingLabel={t('workspaceHome.history.awaitingInput')}
            onSelect={onSelect}
            onDelete={onDelete}
            onRename={onRename}
          />
        ))}
      </Flex>
    )
  }

  return (
    <Flex vertical style={{ height: '100%', minHeight: 0 }}>
      {searchable ? (
        <div className={styles.historySearch}>
          <Input
            allowClear
            autoFocus
            size="small"
            value={search}
            prefix={<Search size={14} className={styles.historySearchIcon} />}
            placeholder={t('workspaceHome.history.searchPlaceholder')}
            aria-label={t('workspaceHome.history.searchPlaceholder')}
            onChange={(event) => onSearchChange?.(event.target.value)}
          />
        </div>
      ) : null}
      <div ref={setScrollRef} className={cx(fadeClassName, styles.historyList)}>
        {loading && conversations.length === 0 ? (
          <ChatHistorySkeletonRows />
        ) : conversations.length === 0 ? (
          <Empty
            description={t(
              searching
                ? 'workspaceHome.history.noResults'
                : 'workspaceHome.history.empty'
            )}
            className={styles.historyEmpty}
          />
        ) : (
          <Flex vertical>
            {renderGroup(t('workspaceHome.history.today'), groups.today)}
            {renderGroup(
              t('workspaceHome.history.yesterday'),
              groups.yesterday
            )}
            {renderGroup(t('workspaceHome.history.older'), groups.older)}
            {loadingMore ? <ChatHistorySkeletonRows /> : null}
          </Flex>
        )}
        {observerElement}
      </div>
    </Flex>
  )
}
